import type {Icon} from '@primer/octicons-react'
import {
  AgentIcon,
  AiModelIcon,
  BookIcon,
  CheckCircleIcon,
  ChecklistIcon,
  CodeIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  CreditCardIcon,
  GearIcon,
  GitPullRequestIcon,
  GraphIcon,
  HeartIcon,
  HomeIcon,
  IssueOpenedIcon,
  KeyIcon,
  LawIcon,
  LockIcon,
  MirrorIcon,
  OrganizationIcon,
  PackageIcon,
  PeopleIcon,
  PersonIcon,
  PlayIcon,
  PlugIcon,
  RepoForkedIcon,
  RepoIcon,
  ShieldIcon,
  StarIcon,
  TableIcon,
} from '@primer/octicons-react'

/**
 * Valid octicon names for navigation items (breadcrumbs and tabs)
 */
export type OcticonName =
  | 'agent'
  | 'ai-model'
  | 'book'
  | 'check-circle'
  | 'checklist'
  | 'code'
  | 'comment-discussion'
  | 'copilot'
  | 'credit-card'
  | 'gear'
  | 'git-pull-request'
  | 'graph'
  | 'heart'
  | 'home'
  | 'issue-opened'
  | 'key'
  | 'law'
  | 'lock'
  | 'mirror'
  | 'organization'
  | 'package'
  | 'people'
  | 'person'
  | 'play'
  | 'plug'
  | 'repo'
  | 'repo-forked'
  | 'shield'
  | 'star'
  | 'table'

/**
 * Map of octicon names to their React components.
 * Used for rendering icons in breadcrumb navigation and local navigation tabs.
 */
export const octiconMap: Record<OcticonName, Icon> = {
  agent: AgentIcon,
  'ai-model': AiModelIcon,
  book: BookIcon,
  'check-circle': CheckCircleIcon,
  checklist: ChecklistIcon,
  code: CodeIcon,
  'comment-discussion': CommentDiscussionIcon,
  copilot: CopilotIcon,
  'credit-card': CreditCardIcon,
  gear: GearIcon,
  'git-pull-request': GitPullRequestIcon,
  graph: GraphIcon,
  heart: HeartIcon,
  home: HomeIcon,
  'issue-opened': IssueOpenedIcon,
  key: KeyIcon,
  law: LawIcon,
  lock: LockIcon,
  mirror: MirrorIcon,
  organization: OrganizationIcon,
  package: PackageIcon,
  people: PeopleIcon,
  person: PersonIcon,
  play: PlayIcon,
  plug: PlugIcon,
  repo: RepoIcon,
  'repo-forked': RepoForkedIcon,
  shield: ShieldIcon,
  star: StarIcon,
  table: TableIcon,
} as const

/**
 * Get the octicon component for a navigation item.
 * @param iconName - The name of the icon to retrieve
 * @returns The octicon React component, or undefined if not found
 */
export function getOcticon(iconName?: OcticonName): Icon | undefined {
  const icon = iconName ? octiconMap[iconName] : undefined

  if (iconName && !icon) {
    // Report missing icon to Failbot, but don't block rendering
    let errorMessage = `Octicon not found: ${iconName}`

    // use a more detailed error message in non-production environments
    if (process.env.NODE_ENV !== 'production') {
      errorMessage = `Octicon "${iconName}" is not mapped in packages/global-navigation/utils/octicons.ts. Please ensure it is added to the global navigation octicon map.`
    }

    setTimeout(() => {
      throw new Error(errorMessage)
    }, 0)
  }

  return icon
}
